import React, { useEffect, useState, useMemo } from 'react';
import client from '../api/client';

interface BPFile {
  filename: string;
  displayName: string;
  dateKey: string;
  sortDate: number;
  groupKey: string;
  fileLabel: string;
}

interface BPGroup {
  groupKey: string;
  groupTitle: string;
  dateKey: string;
  sortDate: number;
  files: BPFile[];
}

interface ModalState {
  group: BPGroup;
  summary: string | null;
  loadingSummary: boolean;
}

const MONTHS_ORDER = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const KNOWN_REGS = ['ASIC','FCA','OSC','ESMA','COAC','MAS'] as const;
const REG_ORDER = ['RDF','ASIC','FCA','OSC','ESMA','COAC','MAS'];

function inferRegulator(filename: string): string {
  const f = filename.toUpperCase();
  for (const r of KNOWN_REGS) if (f.includes(r)) return r;
  return 'RDF';
}

function getRegulatorBadge(filename: string): { label: string; bg: string; color: string } {
  const f = filename.toUpperCase();
  if (f.includes('ASIC')) return { label: 'ASIC', bg: '#0055a5', color: '#fff' };
  if (f.includes('FCA'))  return { label: 'FCA',  bg: '#003087', color: '#fff' };
  if (f.includes('OSC'))  return { label: 'OSC',  bg: '#005c2e', color: '#fff' };
  if (f.includes('ESMA')) return { label: 'ESMA', bg: '#003399', color: '#fff' };
  if (f.includes('COAC')) return { label: 'COAC', bg: '#555',    color: '#fff' };
  return { label: 'RDF', bg: 'var(--c-accent)', color: '#fff' };
}

function RegulatorIcon({ filename }: { filename: string }) {
  const badge = getRegulatorBadge(filename);
  return (
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 8,
        background: badge.bg,
        color: badge.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        flexShrink: 0,
      }}
    >
      {badge.label}
    </div>
  );
}

async function openFile(path: string, _filename?: string) {
  const response = await client.get(path, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: response.data.type });
  const url = URL.createObjectURL(blob);
  const isMobile = window.innerWidth <= 768;
  if (isMobile) {
    window.location.href = url;
  } else {
    window.open(url, '_blank');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function deriveGroupTitle(groupKey: string): string {
  const match = groupKey.match(/^(.*)\s+-\s+[A-Za-z]+\s+\d{4}$/);
  return match ? match[1] : groupKey;
}

export const BestPracticesPage: React.FC = () => {
  const [groups, setGroups] = useState<BPGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [filterRegulator, setFilterRegulator] = useState<string | null>(null);
  const [filterMonth, setFilterMonth] = useState<string | null>(null);
  const [filterYear, setFilterYear] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [summaries, setSummaries] = useState<Record<string, string | null>>({});

  useEffect(() => {
    client
      .get<{ summaries: Record<string, string | null> }>('/best-practices/summaries')
      .then(({ data }) => setSummaries(data.summaries))
      .catch(() => {});
  }, []);

  useEffect(() => {
    client
      .get<{ files: BPFile[] }>('/best-practices/files')
      .then(({ data }) => {
        const map = new Map<string, BPGroup>();
        for (const f of data.files) {
          if (!map.has(f.groupKey)) {
            map.set(f.groupKey, {
              groupKey: f.groupKey,
              groupTitle: deriveGroupTitle(f.groupKey),
              dateKey: f.dateKey,
              sortDate: f.sortDate,
              files: [],
            });
          }
          map.get(f.groupKey)!.files.push(f);
        }
        const sorted = Array.from(map.values()).sort((a, b) => {
          if (a.dateKey === 'undated') return 1;
          if (b.dateKey === 'undated') return -1;
          return b.sortDate - a.sortDate;
        });
        setGroups(sorted);
      })
      .catch(() => setError('Failed to load best practices. Please try again later.'))
      .finally(() => setLoading(false));
  }, []);

  const availableRegulators = useMemo(() => {
    const seen = new Set(groups.map(g => inferRegulator(g.files[0].filename)));
    return REG_ORDER.filter(r => seen.has(r));
  }, [groups]);

  const availableYears = useMemo(() => {
    const source = filterMonth !== null
      ? groups.filter(g => g.dateKey !== 'undated' && g.dateKey.split(' ')[0] === filterMonth)
      : groups;
    return [...new Set(source.filter(g => g.sortDate > 0).map(g => Math.floor(g.sortDate / 100)))].sort((a, b) => a - b);
  }, [groups, filterMonth]);

  const availableMonths = useMemo(() => {
    const source = filterYear !== null
      ? groups.filter(g => g.sortDate > 0 && Math.floor(g.sortDate / 100) === filterYear)
      : groups;
    const seen = new Set(source.filter(g => g.dateKey !== 'undated').map(g => g.dateKey.split(' ')[0]));
    return MONTHS_ORDER.filter(m => seen.has(m));
  }, [groups, filterYear]);

  const filteredGroups = useMemo(() => groups.filter(g => {
    if (filterRegulator !== null && inferRegulator(g.files[0].filename) !== filterRegulator) return false;
    if (filterMonth !== null) {
      if (g.dateKey === 'undated') return false;
      if (g.dateKey.split(' ')[0] !== filterMonth) return false;
    }
    if (filterYear !== null) {
      if (g.sortDate === 0) return false;
      if (Math.floor(g.sortDate / 100) !== filterYear) return false;
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      const inTitle = g.groupTitle.toLowerCase().includes(q) || g.dateKey.toLowerCase().includes(q);
      const inSummary = g.files.some(f => (summaries[f.filename] ?? '').toLowerCase().includes(q));
      if (!inTitle && !inSummary) return false;
    }
    return true;
  }), [groups, filterRegulator, filterMonth, filterYear, searchText, summaries]);

  const openModal = (group: BPGroup) => {
    setModal({ group, summary: null, loadingSummary: true });
    const mainPdf = group.files.find(f => /\.pdf$/i.test(f.filename)) || group.files[0];
    client
      .get<{ summary: string | null }>(`/best-practices/summary/${encodeURIComponent(mainPdf.filename)}`)
      .then(({ data }) => setModal(m => m ? { ...m, summary: data.summary, loadingSummary: false } : null))
      .catch(() => setModal(m => m ? { ...m, loadingSummary: false } : null));
  };

  const closeModal = () => setModal(null);

  const handleOpen = async (file: BPFile, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setDownloading(file.filename);
    try {
      await openFile(`/best-practices/files/${encodeURIComponent(file.filename)}`, file.filename);
    } catch {
      alert('Could not open file. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--c-text-muted)' }}>
        <p style={{ fontSize: 15 }}>Loading best practices...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--c-text-muted)' }}>
        <p style={{ fontSize: 15 }}>{error}</p>
      </div>
    );
  }

  const filtersActive = filterRegulator !== null || filterMonth !== null || filterYear !== null || searchText.trim() !== '';

  return (
    <>
      {/* Page heading */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 4 }}>Best Practice</h2>
        <p style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>
          Regulatory guidance and best practice documents from RDF members.
        </p>
      </div>

      {/* Filter bars */}
      {(availableRegulators.length > 1 || availableYears.length > 1 || availableMonths.length > 1) && (
        <div style={{ marginBottom: 20 }}>
          {availableRegulators.length > 1 && (
            <div className="filter-bar">
              <button className={`filter-btn${filterRegulator === null ? ' active' : ''}`} onClick={() => setFilterRegulator(null)}>All</button>
              {availableRegulators.map(r => (
                <button key={r} className={`filter-btn${filterRegulator === r ? ' active' : ''}`} onClick={() => setFilterRegulator(filterRegulator === r ? null : r)}>{r}</button>
              ))}
            </div>
          )}
          {availableYears.length > 1 && (
            <div className="filter-bar">
              <button className={`filter-btn${filterYear === null ? ' active' : ''}`} onClick={() => setFilterYear(null)}>All</button>
              {availableYears.map(y => (
                <button key={y} className={`filter-btn${filterYear === y ? ' active' : ''}`} onClick={() => setFilterYear(filterYear === y ? null : y)}>{y}</button>
              ))}
            </div>
          )}
          {availableMonths.length > 1 && (
            <div className="filter-bar">
              <button className={`filter-btn${filterMonth === null ? ' active' : ''}`} onClick={() => setFilterMonth(null)}>All</button>
              {availableMonths.map(m => (
                <button key={m} className={`filter-btn${filterMonth === m ? ' active' : ''}`} onClick={() => setFilterMonth(filterMonth === m ? null : m)}>{m}</button>
              ))}
            </div>
          )}
          <input
            type="text"
            placeholder="Search best practice content… e.g. UPI, EMIR, collateral"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{
              width: '100%',
              padding: '7px 12px',
              fontSize: 13,
              border: '1px solid var(--c-border)',
              borderRadius: 'var(--r)',
              background: 'var(--c-surface)',
              color: 'var(--c-text)',
              outline: 'none',
              marginTop: 4,
            }}
          />
        </div>
      )}

      {filteredGroups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--c-text-muted)' }}>
          <p style={{ fontSize: 15 }}>{filtersActive ? 'No best practices found for the selected filters.' : 'No best practices found.'}</p>
        </div>
      ) : (
        <div className="doc-grid">
          {filteredGroups.map((g) => (
            <div
              key={g.groupKey}
              className="doc-card"
              onClick={() => openModal(g)}
              style={{ cursor: 'pointer', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                <RegulatorIcon filename={g.files[0].filename} />
                <div className="doc-info" style={{ flex: 1 }}>
                  <div className="doc-title">{g.groupTitle}</div>
                  <div className="doc-meta">{g.dateKey === 'undated' ? '' : g.dateKey}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                {g.files.map(f => (
                  <button
                    key={f.filename}
                    onClick={(e) => handleOpen(f, e)}
                    disabled={downloading === f.filename}
                    className="badge badge-neutral"
                    style={{ whiteSpace: 'nowrap', cursor: 'pointer', border: 'none' }}
                  >
                    {downloading === f.filename ? '...' : f.fileLabel}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: 16 }}>{modal.group.groupTitle}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {modal.loadingSummary ? (
                <p style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>Loading summary...</p>
              ) : modal.summary ? (
                <div style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-line' }}>{modal.summary}</div>
              ) : (
                <p style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>Summary not available</p>
              )}
            </div>
            <div className="modal-footer">
              {modal.group.files.map(f => (
                <button
                  key={f.filename}
                  onClick={(e) => handleOpen(f, e)}
                  disabled={downloading === f.filename}
                  className="badge badge-neutral"
                  style={{ cursor: 'pointer', border: 'none' }}
                >
                  {downloading === f.filename ? '...' : f.fileLabel}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
